// @refresh reset

import { lazy, Suspense } from 'react';
import MainLayout from 'layout/Dashboard';
import AuthGuard from 'components/AuthGuard';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import DynamicRoute from './DynamicRoute';

// Panel full-screen GeoAudit (fuera del MainLayout: sin sidebar/header del
// backoffice). Los usuarios "tipo dashboard" (keyProfile === PROFILE.DASHBOARD)
// aterrizan aquí directo tras login. Ver views/dashboard-geoaudit.
const GeoAuditPanel = lazy(() => import('views/dashboard-geoaudit'));

// Detalle de obra (/works/:key) — página dedicada DENTRO del backoffice
// (con sidebar). No es menu item; se navega desde la lista de Obras.
//
// Son TRES vistas independientes y el reparto por perfil lo hace
// DetailRouter (2026-10-16): administración y ASF, Coordinador de
// procesamiento, y Dronero. El auditor tiene la suya en /audit/:key.
const WorkDetail = lazy(() => import('views/works/DetailRouter'));
const DroneroDetail = lazy(() => import('views/pilot/Detail'));
// Mesa de trabajo del auditor (/audit/:key): vista SOLO del auditor, separada
// del detalle general para poder modificarla sin tocar a los demás perfiles.
const WorkAudit = lazy(() => import('views/auditor/WorkAudit'));
const ProcessingLoad = lazy(() => import('views/processing/Load'));
// Verificación de constancias de auditoría (db/176): a donde lleva el QR. Con sesión.
const VerifyCertificate = lazy(() => import('views/certificates/Verify'));

// ==============================|| MAIN ROUTES ||============================== //
//
// Router dinámico (2026-05-24): las rutas dejaron de estar hardcoded.
// La fuente de verdad de qué URLs existen y qué componente monta cada una
// es la tabla `sys_menu_items`:
//
//   url       → la URL pública (editable libremente desde la admin del menú).
//   view_key  → identificador estable del componente (inmutable por código,
//               resuelto contra backoffice/src/routes/viewRegistry.ts).
//   id_permission → permiso requerido para acceder (RoutePermissionGuard).
//
// Cualquier path bajo "/" cae en DynamicRoute y resuelve el componente
// consultando el menuStore.
//
// Sin redirects legacy de URLs viejas: corte limpio. Ver
// feat_2026-05-24_rediseno-navegacion-backoffice.md (consolida el cambio).

const MainRoutes = {
  path: '/',
  element: <AuthGuard />,
  errorElement: <ErrorBoundary />,
  children: [
    // Panel full-screen (sin MainLayout). Ruta estática: gana en specificity
    // sobre el catch-all '*' de abajo. Sigue protegido por AuthGuard.
    {
      path: 'panel',
      element: (
        <Suspense fallback={<Loader />}>
          <GeoAuditPanel />
        </Suspense>
      ),
    },
    {
      element: <MainLayout />,
      children: [
        // Detalle de obra: ruta estática con param, gana sobre el catch-all '*'.
        { path: 'works/:key', element: (<Suspense fallback={<Loader />}><WorkDetail /></Suspense>) },
        { path: 'audit/:key', element: (<Suspense fallback={<Loader />}><WorkAudit /></Suspense>) },
        // Obra del dronero: pantalla dedicada, no modal. Se llega dando clic
        // al renglón en "Mis vuelos". Vista propia en views/pilot/.
        { path: 'my-flights/:key', element: (<Suspense fallback={<Loader />}><DroneroDetail /></Suspense>) },
        // Procesamiento: carga del MATERIAL PROCESADO de una obra (ortos por pestaña + modelo 3D).
        // No es la evidencia de campo del dronero. Se llega desde "Cargar material" en Procesamiento.
        { path: 'processing/:key', element: (<Suspense fallback={<Loader />}><ProcessingLoad /></Suspense>) },
        { path: 'certificates/verify', element: (<Suspense fallback={<Loader />}><VerifyCertificate /></Suspense>) },
        { path: 'certificates/verify/:folio', element: (<Suspense fallback={<Loader />}><VerifyCertificate /></Suspense>) },
        // Catch-all: el componente se decide en runtime contra sys_menu_items.
        { path: '*', element: <DynamicRoute /> },
        { index: true, element: <DynamicRoute /> },
      ]
    }
  ]
};

export default MainRoutes;
